import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import {
  t206Api,
  FT160,
  FT161,
  FT162,
  FT163,
  FT164,
  FT162Input,
  FT163Input,
  CreateT206Request,
  UpdateT206Request,
} from '../../../services/t206Api';
import { T206Mode } from './useT206Mode';

// ---- Form data shape ----

export interface FT163FormItem {
  ft163_11: string;
  ft163_12: string;
  ft163_13: string;
  ft163_15: string;
  ft163_15a: string;
  ft163_16: string;
  ft163_16a: string;
}

export interface FT162FormItem {
  ft162_11: string;
  ft162_12: string;
  ft162_13: string;
  tracking_items: FT163FormItem[];
}

export interface FT161FormItem {
  ft161_11: string;
  ft161_11a: string;
  ft161_11b: string;
  ft161_12: string;
  ft161_12a: string;
}

export interface FT164FormItem {
  ft164_10: string;
  ft164_11: string;
  ft164_13: string;
  ft164_14: string;
}

export interface T206FormData {
  ft160_11: string;   // 會議主題
  ft160_12: string;   // 說明
  ft160_13: string;   // 會議日期
  ft160_14: string;   // 開始時間 (HH:MM:SS)
  ft160_15: string;   // 結束時間 (HH:MM:SS)
  ft160_16: string;   // 會議地點
  ft160_18: number;   // 我司參加人數 (readonly, auto-calculated)
  ft160_19: number;   // 議題數 (readonly, auto-calculated)
  ft160_22: string;   // 記錄人代碼
  ft160_25: number;   // 客戶參加人數 (readonly, auto-calculated)
  our_attendees: FT161FormItem[];
  topics: FT162FormItem[];
  customer_attendees: FT164FormItem[];
}

const emptyForm = (): T206FormData => ({
  ft160_11: '',
  ft160_12: '',
  ft160_13: dayjs().format('YYYY-MM-DD'),
  ft160_14: '',
  ft160_15: '',
  ft160_16: '',
  ft160_18: 0,
  ft160_19: 0,
  ft160_22: '',
  ft160_25: 0,
  our_attendees: [],
  topics: [],
  customer_attendees: [],
});

function recordToForm(r: FT160): T206FormData {
  return {
    ft160_11: r.ft160_11 || '',
    ft160_12: r.ft160_12 || '',
    ft160_13: r.ft160_13 ? dayjs(r.ft160_13).format('YYYY-MM-DD') : '',
    ft160_14: r.ft160_14 || '',
    ft160_15: r.ft160_15 || '',
    ft160_16: r.ft160_16 || '',
    ft160_18: r.ft160_18 || 0,
    ft160_19: r.ft160_19 || 0,
    ft160_22: r.ft160_22 || '',
    ft160_25: r.ft160_25 || 0,
    our_attendees: (r.our_attendees || []).map((a: FT161) => ({
      ft161_11: a.ft161_11 || '',
      ft161_11a: a.ft161_11a || '',
      ft161_11b: a.ft161_11b || '',
      ft161_12: a.ft161_12 || '',
      ft161_12a: a.ft161_12a || '',
    })),
    topics: (r.topics || []).map((t: FT162) => ({
      ft162_11: t.ft162_11 || '',
      ft162_12: t.ft162_12 || '',
      ft162_13: t.ft162_13 || '',
      tracking_items: (t.tracking_items || []).map((tr: FT163) => ({
        ft163_11: tr.ft163_11 || '',
        ft163_12: tr.ft163_12 ? dayjs(tr.ft163_12).format('YYYY-MM-DD') : '',
        ft163_13: tr.ft163_13 || '',
        ft163_15: tr.ft163_15 || '',
        ft163_15a: tr.ft163_15a || '',
        ft163_16: tr.ft163_16 || '',
        ft163_16a: tr.ft163_16a || '',
      })),
    })),
    customer_attendees: (r.customer_attendees || []).map((ca: FT164) => ({
      ft164_10: ca.ft164_10 || '',
      ft164_11: ca.ft164_11 || '',
      ft164_13: ca.ft164_13 || '',
      ft164_14: ca.ft164_14 || '',
    })),
  };
}

function formToRequest(form: T206FormData): CreateT206Request {
  const topics: FT162Input[] = form.topics.map(t => ({
    ft162_11: t.ft162_11,
    ft162_12: t.ft162_12,
    ft162_13: t.ft162_13,
    tracking_items: t.tracking_items.map((tr): FT163Input => ({
      ft163_11: tr.ft163_11,
      ft163_12: tr.ft163_12 || undefined,
      ft163_13: tr.ft163_13,
      ft163_15: tr.ft163_15,
      ft163_15a: tr.ft163_15a,
      ft163_16: tr.ft163_16,
      ft163_16a: tr.ft163_16a,
    })),
  }));

  return {
    ft160_11: form.ft160_11,
    ft160_12: form.ft160_12,
    ft160_13: form.ft160_13,
    ft160_14: form.ft160_14,
    ft160_15: form.ft160_15,
    ft160_16: form.ft160_16,
    ft160_22: form.ft160_22,
    our_attendees: form.our_attendees,
    topics,
    customer_attendees: form.customer_attendees,
  };
}

// ---- Hook ----

export function useT206Record(mode: T206Mode, enterBrowse: () => void) {
  const [records, setRecords] = useState<FT160[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [formData, setFormData] = useState<T206FormData>(emptyForm());
  const [loading, setLoading] = useState(false);

  const current: FT160 | null =
    currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;

  const loadRecords = useCallback(async (keyword?: string): Promise<FT160[]> => {
    setLoading(true);
    try {
      const res = await t206Api.list(1, 500, keyword);
      const data = res.data || [];
      setRecords(data);
      return data;
    } catch {
      message.error('載入資料失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      setFormData(recordToForm(current));
    }
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  const navFirst = useCallback(() => {
    if (records.length > 0) setCurrentIdx(0);
  }, [records.length]);

  const navPrev = useCallback(() => {
    if (currentIdx > 0) setCurrentIdx(prev => prev - 1);
  }, [currentIdx]);

  const navNext = useCallback(() => {
    if (currentIdx < records.length - 1) setCurrentIdx(prev => prev + 1);
  }, [currentIdx, records.length]);

  const navLast = useCallback(() => {
    if (records.length > 0) setCurrentIdx(records.length - 1);
  }, [records.length]);

  const startAdd = useCallback(() => {
    setFormData(emptyForm());
  }, []);

  const startEdit = useCallback(() => {
    if (current) {
      setFormData(recordToForm(current));
    }
  }, [current]);

  const cancel = useCallback(() => {
    if (current) {
      setFormData(recordToForm(current));
    } else {
      setFormData(emptyForm());
    }
    enterBrowse();
  }, [current, enterBrowse]);

  const save = useCallback(async () => {
    setLoading(true);
    try {
      let savedId = '';
      const req = formToRequest(formData);
      if (mode === 'add') {
        const res = await t206Api.create(req as CreateT206Request);
        savedId = res.ft160_01;
        message.success('新增成功');
      } else if (mode === 'edit' && current) {
        await t206Api.update(current.ft160_01, req as UpdateT206Request);
        savedId = current.ft160_01;
        message.success('修改成功');
      }
      const data = await loadRecords();
      const idx = data.findIndex(r => r.ft160_01 === savedId);
      setCurrentIdx(idx >= 0 ? idx : 0);
      enterBrowse();
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [mode, formData, current, loadRecords, enterBrowse]);

  const doDelete = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    try {
      await t206Api.delete(current.ft160_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
      enterBrowse();
    } catch (err: any) {
      const msg = err?.response?.data?.error || '刪除失敗';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [current, currentIdx, loadRecords, enterBrowse]);

  const quickSearch = useCallback(async (keyword: string) => {
    setLoading(true);
    try {
      const data = await loadRecords(keyword);
      if (data.length > 0) {
        setCurrentIdx(0);
      } else {
        setCurrentIdx(-1);
        message.info('查無資料');
      }
    } catch {
      message.error('查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [loadRecords]);

  const setField = useCallback(<K extends keyof T206FormData>(
    field: K,
    value: T206FormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  return {
    records,
    current,
    currentIdx,
    formData,
    loading,
    setField,
    navFirst,
    navPrev,
    navNext,
    navLast,
    startAdd,
    startEdit,
    cancel,
    save,
    doDelete,
    quickSearch,
    loadRecords,
  };
}
